import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { devFundAPI } from '@/lib/api';
import {
    Sparkles, TrendingUp, Loader2, Calendar as CalendarIcon,
    ChevronDown, ChevronUp, Info,
} from 'lucide-react';
import { toast } from 'sonner';

const DIRIGEANT_EMAILS = ['jeremy@valnexo.fr', 'morgan@valnexo.fr', 'laurent@valnexo.fr'];

const PERIODS = [
    { key: 'month', label: 'Ce mois-ci' },
    { key: 'last_month', label: 'Mois dernier' },
    { key: 'total', label: 'Depuis le début' },
    { key: 'custom', label: 'Période perso' },
];

const formatEuro = (n) => new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
}).format(n || 0);

/**
 * DevFundCard — Jauge "Développement Valnexo".
 *
 * Cumule l'écart de partage 65/35 vs 50/50 sur les dossiers EP gagnés
 * dont l'équipe n'a PAS atteint son palier Prime de secteur ce mois-là.
 * Visible uniquement pour les 3 dirigeants (Jérémy, Morgan, Laurent).
 */
export default function DevFundCard({ user }) {
    const email = (user?.email || '').toLowerCase();
    const isDirigeant = DIRIGEANT_EMAILS.includes(email);

    const [period, setPeriod] = useState('month');
    const [customStart, setCustomStart] = useState(null);
    const [customEnd, setCustomEnd] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showBreakdown, setShowBreakdown] = useState(false);
    // Réductible : compact par défaut, l'utilisateur agrandit à la demande
    const [expanded, setExpanded] = useState(false);

    const load = useCallback(async () => {
        if (period === 'custom' && (!customStart || !customEnd)) return;
        setLoading(true);
        try {
            const params = { period };
            if (period === 'custom') {
                params.start = customStart.toISOString().slice(0, 10);
                params.end = customEnd.toISOString().slice(0, 10);
            }
            const res = await devFundAPI.get(params);
            setData(res.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible de charger la jauge Développement");
        } finally {
            setLoading(false);
        }
    }, [period, customStart, customEnd]);

    useEffect(() => { if (isDirigeant) load(); }, [isDirigeant, load]);

    if (!isDirigeant) return null;

    // ================ VUE COMPACTE (par défaut) ================
    if (!expanded) {
        return (
            <button
                type="button"
                onClick={() => setExpanded(true)}
                data-testid="dev-fund-card-compact"
                className="w-full group relative overflow-hidden rounded-xl border-2 border-emerald-300 bg-gradient-to-r from-slate-900 via-emerald-950 to-slate-900 text-white shadow-md hover:shadow-lg transition-shadow"
            >
                <div className="flex items-center gap-3 px-4 py-3">
                    <div className="w-9 h-9 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center shrink-0">
                        <TrendingUp className="w-4 h-4 text-emerald-200" />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-white truncate">Fonds Développement Valnexo</span>
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
                                DIRIGEANTS
                            </span>
                        </div>
                        <div className="text-[11px] text-emerald-100/70 mt-0.5">
                            {loading ? 'Chargement…' : `${formatEuro(data?.total_dev || 0)} · ${data?.dossier_dev_count || 0} dossier${(data?.dossier_dev_count || 0) > 1 ? 's' : ''} · ce mois-ci`}
                        </div>
                    </div>
                    <ChevronDown className="w-4 h-4 text-emerald-200/70 group-hover:text-emerald-100 transition-colors shrink-0" />
                </div>
            </button>
        );
    }

    // ================ VUE ÉLARGIE ================
    return (
        <Card
            className="relative overflow-hidden border-2 border-emerald-300 bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 text-white shadow-lg"
            data-testid="dev-fund-card"
        >
            <div className="absolute top-3 right-12 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] font-bold shadow">
                <Sparkles className="w-3 h-3" />
                DIRIGEANTS
            </div>
            <button
                type="button"
                onClick={() => setExpanded(false)}
                className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-slate-800/60 hover:bg-slate-700/80 border border-slate-600 flex items-center justify-center text-slate-300 hover:text-white transition-colors"
                aria-label="Réduire"
                data-testid="dev-fund-collapse-btn"
            >
                <ChevronUp className="w-3.5 h-3.5" />
            </button>

            <CardContent className="p-5 sm:p-6">
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-11 h-11 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center shrink-0">
                        <TrendingUp className="w-5 h-5 text-emerald-200" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="text-lg font-bold text-white">
                            Fonds Développement Valnexo
                        </h2>
                        <p className="text-xs text-emerald-100/70 mt-0.5 max-w-xl">
                            Écart de partage 65/35 vs 50/50 sur les dossiers Courtage dont l'équipe
                            n'a PAS atteint son palier Prime de secteur ce mois-là.
                        </p>
                    </div>
                </div>

                {/* Filtre période */}
                <div className="flex items-center gap-1.5 flex-wrap mb-4">
                    {PERIODS.map((p) => (
                        <button
                            key={p.key}
                            onClick={() => setPeriod(p.key)}
                            data-testid={`dev-fund-period-${p.key}`}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${period === p.key ? 'bg-emerald-500 border-emerald-400 text-white' : 'bg-slate-800/60 border-slate-600 text-slate-300 hover:bg-slate-700/70'}`}
                        >
                            {p.label}
                        </button>
                    ))}
                    {period === 'custom' && (
                        <>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-8 bg-slate-800/60 border-slate-600 text-slate-100 hover:bg-slate-700">
                                        <CalendarIcon className="w-3 h-3 mr-1.5" />
                                        {customStart ? customStart.toLocaleDateString('fr-FR') : 'Début'}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0">
                                    <Calendar mode="single" selected={customStart} onSelect={setCustomStart} initialFocus />
                                </PopoverContent>
                            </Popover>
                            <span className="text-xs text-slate-400">→</span>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-8 bg-slate-800/60 border-slate-600 text-slate-100 hover:bg-slate-700">
                                        <CalendarIcon className="w-3 h-3 mr-1.5" />
                                        {customEnd ? customEnd.toLocaleDateString('fr-FR') : 'Fin'}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0">
                                    <Calendar mode="single" selected={customEnd} onSelect={setCustomEnd} initialFocus />
                                </PopoverContent>
                            </Popover>
                        </>
                    )}
                </div>

                {/* Jauge */}
                {loading ? (
                    <div className="py-8 text-center text-emerald-200/70 flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Calcul en cours…
                    </div>
                ) : (
                    <>
                        <div className="flex items-end gap-6 flex-wrap">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-emerald-200/60 mb-1">
                                    Cumul développement
                                </div>
                                <div className="text-4xl sm:text-5xl font-black text-emerald-300 tabular-nums leading-none" data-testid="dev-fund-total">
                                    {formatEuro(data?.total_dev)}
                                </div>
                                <div className="text-xs text-emerald-100/60 mt-1">
                                    {data?.dossier_dev_count || 0} dossier{(data?.dossier_dev_count || 0) > 1 ? 's' : ''} sans bonus atteint
                                </div>
                            </div>
                            {data?.total_bonus_ecart > 0 && (
                                <div className="rounded-lg bg-slate-800/60 border border-slate-700 px-3 py-2 min-w-[180px]">
                                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">
                                        Reversé aux équipes (bonus)
                                    </div>
                                    <div className="text-lg font-bold text-slate-100 tabular-nums">
                                        {formatEuro(data.total_bonus_ecart)}
                                    </div>
                                    <div className="text-[11px] text-slate-500">
                                        {data.dossier_bonus_count} dossier{data.dossier_bonus_count > 1 ? 's' : ''}
                                    </div>
                                </div>
                            )}
                        </div>

                        {data?.breakdown?.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-slate-700/60">
                                <button
                                    onClick={() => setShowBreakdown((v) => !v)}
                                    data-testid="dev-fund-toggle-breakdown"
                                    className="text-xs text-emerald-200/80 hover:text-emerald-100 flex items-center gap-1.5"
                                >
                                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showBreakdown ? 'rotate-180' : ''}`} />
                                    {showBreakdown ? 'Masquer' : 'Détail par dossier'} ({data.breakdown.length})
                                </button>
                                {showBreakdown && (
                                    <div className="mt-3 max-h-72 overflow-y-auto rounded-md border border-slate-700/60 divide-y divide-slate-700/40">
                                        {data.breakdown.map((b) => (
                                            <div key={b.prospect_id} className="px-3 py-2 flex items-center gap-3 text-xs">
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium text-white truncate">{b.client_name}</div>
                                                    <div className="text-[11px] text-slate-400">
                                                        {new Date(b.won_date).toLocaleDateString('fr-FR')} · CG {formatEuro(b.commission_ht)}
                                                    </div>
                                                </div>
                                                {b.bonus_atteint ? (
                                                    <Badge variant="outline" className="bg-slate-800 border-slate-600 text-slate-300 text-[10px]">
                                                        Bonus reversé
                                                    </Badge>
                                                ) : (
                                                    <Badge className="bg-emerald-600 text-white text-[10px]">
                                                        + {formatEuro(b.contribution_dev)}
                                                    </Badge>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="mt-3 flex items-start gap-2 text-[11px] text-emerald-100/50">
                            <Info className="w-3 h-3 mt-0.5 shrink-0" />
                            <span>
                                Formule : 15 % du reste après VP+Apporteur, sur chaque dossier EP gagné depuis le 01/07/2026.
                                L'excédent des 5 % (VP {'>'} seuil 30 %) pris en charge par Valnexo n'est PAS compté.
                            </span>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
